import type {AsyncDataOptions} from '#app';
import type {Comment, PaginatedDataResult, Poll, Post} from "~/repository/types";

// locals
import FetchFactory from '../factory';
import CommentModel from "~/repository/models/Comment";
import PostModel from "~/repository/models/Post";


class PostsModule extends FetchFactory {
    private RESOURCE = '/posts';

    async search(
        search: string,
        page: number | undefined,
    ) {
        return this.call<PaginatedDataResult<Post>>(
            'GET',
            `${this.RESOURCE}`,
            {
                search: search,
                page: page
            }
        )
    }

    /**
     * Return the posts as an array
     * @param page
     * @returns
     */
    public async getPosts(page: number | undefined) {
        return this.call<PaginatedDataResult<Post>>(
            'GET',
            `${this.RESOURCE}`,
            {
                page: page
            }
        )
    }

    public async getFeed(page: number | undefined) {
        return this.call<PaginatedDataResult<Post>>(
            'GET',
            `feed`,
            {
                page: page
            }
        )
    }


    async getProfilePosts(
        username: string,
        page: number | undefined,
        asyncDataOptions?: AsyncDataOptions<PaginatedDataResult<Post>>
    ) {
        return this.call<PaginatedDataResult<Post>>(
            'GET',
            `${this.RESOURCE}/user/${username}`,
            {
                page: page
            }
        )
    }
 
    async create(message: string | null, media: Array<string>, poll: string[] | null, scope: number, links: Array<number> | undefined, repost_id: number | undefined, price: number | undefined, content_flags: string[] | undefined) {
        return await this.call('post', `${this.RESOURCE}`, {
            message,
            media,
            poll,
            poll_question: poll ? message : undefined,
            scope,
            links,
            repost_id,
            price,
            content_flags,
        })
    }

    /**
     * Like a post
     * @param post
     */
    async like(post: Post) {
        return await this.call<{ is_liked: boolean, likes_count: number }>('post', `${this.RESOURCE}/${post.id}/like`)
    }

    async repost(postId: number) {
        const {post: newPost} = await this.call<{ post: Post }>('post', `${this.RESOURCE}/${postId}/repost`)
        return new PostModel(newPost)
    }

    /**
     * Pin post
     * @param post
     */
    async pin(post: Post) {
        const response = await this.call<{ post: Post }>('POST', `${this.RESOURCE}/${post.id}/pin`);
        return {
            is_pinned: response.is_pinned
        }
    }

    async unpin(post: Post) {
        const response = await this.call<{ post: Post }>('POST', `${this.RESOURCE}/${post.id}/unpin`);
        return {
            is_pinned: response.is_pinned
        }
    }

    async delete(post: Post) {
        await this.call('DELETE', `${this.RESOURCE}/${post.id}`);
        return true;
    }

    /**
     * Vote in Post Poll
     * @param post
     * @param poll
     * @param option
     */
    async vote(post: Post, poll: Poll, option: number) {
        return await this.call<void>('post', `${this.RESOURCE}/${post.id}/${poll.id}/vote`, {
            option
        })
    }


    async getComments(postId: number, page: number | undefined, parent: number | undefined = undefined): Promise<PaginatedDataResult<Comment>> {
        const paginatedData = await this.call<PaginatedDataResult<Comment>>(
            'GET',
            `comments/${postId}`,
            {
                page: page,
                for: parent
            }
        )

        return {
            ...paginatedData,
            data: paginatedData.data.map(CommentModel.create)
        }
    }
}

export default PostsModule;